<template>
  <div class="fill-height pa-0 ma-0" style="background-color:#f5f5f5" fluid>
    <v-content>
      <v-row justify="center" class="px-0 py-5">
        <v-card flat color="#f5f5f5" width="95%" class="mx-auto">
          <p style="text-align:center;" class="pt-6 display-1 font-weight-medium">
            Audit Reports
          </p>
          <p style="text-align:center;" class="body-1 font-weight-normal">
            Find and explore audit reports published by the statutory auditing bodies for each state and territory in Australia.
          </p>

          <!-- Search Bar -->
          <v-row class="px-0 pt-5">
            <v-col cols="12" class="pb-0 mb-0">
              <v-text-field v-model="searchTerm" label="Press Enter or Tab after every keyword" outlined dense solo
                append-icon="mdi-refresh" @keyup.enter="addSearchChip" @blur="addSearchChip"
                @click:append="resetSearch"></v-text-field>
            </v-col>
            <v-col class="pt-0 mt-0 mb-0 pb-0" cols="12">
              <v-chip v-for="(chip, index) in searchChips" :key="index" class="mr-2 mb-5 pt-0 mt-0 px-5"
                color="secondary" label small close @click:close="removeSearchChip(index)">
                {{ chip }}
              </v-chip>
            </v-col>
            <v-col class="pt-0 mt-0" cols="12">
              <v-btn v-if="searchChips.length > 0" class="mr-2 mb-5 pt-0 mt-0 px-5" color="primary" small @click="resetSearch">
                Clear All
              </v-btn>
            </v-col>
          </v-row>

          <v-data-table
            :headers="auditHeaders"
            :items="audits"
            :footer-props="{ 'items-per-page-options': [100] }"
            :items-per-page="100"
            :loading="loading"
            class="elevation-1 py-2"
          >
            <template v-slot:no-data>
              <div v-if="loading" class="text-center py-4">Loading audits...</div>
              <div v-else class="text-center py-4">No data available.</div>
            </template>

            <template v-slot:item.title="{ item }">
              <div class="subtitle-1 pt-3 font-weight-bold">{{ item.title }}</div>
              <div v-html="highlightKeywords(item.matched_keywords)" class="py-3 caption font-italic"></div>
              <div class="pb-5 pt-0">
                <span v-for="tag in consolidateTags(item)" :key="tag.id">
                  <v-chip class="my-1 ml-0 mr-2" color="primary" label text-color="secondary" small outlined>
                    <v-icon icon="mdi-label" small left></v-icon>
                    {{ tag.name }}
                  </v-chip>
                </span>
              </div>
            </template>

            <template v-slot:item.published_date="{ item }">
              {{ formatDate(item.published_date) }}
            </template>

            <template v-slot:item.gov_type="{ item }">
              <v-avatar :color="govAvatarColor(item.gov_type)" size="36" class="mr-2">
                <span class="white--text caption">{{ item.gov_type }}</span>
              </v-avatar>
            </template>

            <template v-slot:item.description="{ item }">
              {{ item.description }}
            </template>

            <template v-slot:item.entities="{ item }">
              <div>
                <p v-for="entity in item.entities" :key="entity.id">{{ entity.name }}</p>
              </div>
            </template>

            <template v-slot:item.findings="{ item }">
              <v-btn
                large icon color="secondary"
                v-if="item.findings"
                text
                @click="selectedAudit = item.title; selectedFindings = item.findings; showFinding = !showFinding"
              >
                <v-icon>mdi-eye-circle-outline</v-icon>
              </v-btn>
            </template>

            <template v-slot:item.conclusion="{ item }">
              <div class="text-body-2 my-4 text-grey-lighten-2">{{ item.conclusion }}</div>
            </template>

            <template v-slot:item.page_url="{ item }">
              <v-tooltip top dark color="#000000" style="opacity: 0.7;">
                <template v-slot:activator="{ on }">
                  <v-btn v-on="on" large icon light :href="item.page_url" target="_blank">
                    <v-icon>mdi-web</v-icon>
                  </v-btn>
                </template>
                <span>Go to Source</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-card>
      </v-row>

      <!-- Findings Modal -->
      <v-dialog width="80%" v-model="showFinding">
        <v-card tile flat class="pa-3">
          <v-card-title>
            <span>
              <v-card-subtitle class="pa-0 font-weight-light">Summary of Findings</v-card-subtitle>
              <span>{{ selectedAudit }}</span>
            </span>
            <div class="flex-grow-1"></div>
            <v-btn icon @click="showFinding = false">
              <v-icon medium>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-simple-table class="pa-3">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left text-subtitle-1 pt-5 font-weight-black">Findings</th>
                  <th class="text-left text-subtitle-1 pt-5 font-weight-black">Recommendations</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="finding in selectedFindings" :key="finding.id">
                  <td class="text-body-2 py-5 pr-5">{{ finding.detail }}</td>
                  <td class="text-body-2 py-5 pl-5">
                    <ul>
                      <li v-for="recommendation in finding.recommendations" :key="recommendation.id">
                        {{ recommendation.text }}
                      </li>
                    </ul>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>
      </v-dialog>
    </v-content>
  </div>
</template>

<script>
import { filterAudits, getAudits } from "./APIManager.js";
import { govColor } from "./Utils.js";

export default {
  data: () => ({
    audits: [],
    showFinding: false,
    selectedAudit: "",
    selectedFindings: [],
    auditHeaders: [
      { text: 'Jurisdiction', value: 'gov_type', align: 'center' },
      { text: 'Audit Title', value: 'title' },
      { text: 'Published', value: 'published_date' },
      { text: 'Summary', value: 'description' },
      { text: 'Related Entities', value: 'entities' },
      { text: 'Findings', value: 'findings', align: 'center', sortable: false },
      { text: 'Conclusion', value: 'conclusion' },
      { text: 'Report', value: 'page_url', align: 'center', sortable: false }
    ],
    loading: false,
    searchTerm: '',
    searchChips: [],
  }),

  mounted() {
    const query = this.$route.query;
    if (query.hasOwnProperty("query")) {
      this.searchChips = this.getArray(JSON.parse(query["query"]));
      this.searchAudits();
    } else {
      this.fetchAudits();
    }
  },

  methods: {
    async fetchAudits() {
      this.loading = true;
      try {
        const response = await getAudits();
        this.audits = response.data.map(item => ({
          ...item.audit,
          matched_keywords: item.matched_keywords,
        }));
      } catch {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },

    async searchAudits() {
      this.loading = true;
      try {
        const response = await filterAudits(JSON.stringify(this.searchChips));
        this.audits = response.data.map(item => ({
          ...item.audit,
          matched_keywords: item.matched_keywords,
        }));
      } catch {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },

    addSearchChip() {
      if (this.searchTerm.trim() !== '') {
        this.searchChips.push(this.searchTerm.trim());
        this.searchTerm = '';
        this.searchAudits();
        this.updateURL();
      }
    },

    removeSearchChip(index) {
      this.searchChips.splice(index, 1);
      this.searchAudits();
      this.updateURL();
    },

    resetSearch() {
      this.searchChips = [];
      this.fetchAudits();
      this.updateURL();
    },

    updateURL() {
      const url = new URL(window.location);
      if (this.searchChips.length === 0) {
        url.searchParams.delete('query');
      } else {
        url.searchParams.set('query', JSON.stringify(this.searchChips));
      }
      window.history.pushState({}, '', url);
    },

    formatDate(inputDate) {
      const date = new Date(inputDate);
      const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
    },

    getArray(string) {
      if (Array.isArray(string)) return string;
      if (string > "") return [string];
      return [];
    },

    consolidateTags(audit) {
      const tags = audit.tags || [];
      const activityTags = audit.activity_tags ? audit.activity_tags.split(',') : [];
      const sectorTags = audit.sector_tags ? audit.sector_tags.split(',') : [];
      const allTags = [...tags, ...activityTags, ...sectorTags];
      return allTags.map((tag, index) => typeof tag === 'object' ? tag : { id: index, name: tag.trim() });
    },

    highlightKeywords(matchedKeywords) {
      return matchedKeywords
        .map(group =>
          group.map(kw =>
            kw.replace(/<em>/g, '<span style="background-color: yellow; color:black">')
              .replace(/<\/em>/g, '</span>')
          ).join(' ')
        ).join(' ');
    },

    govAvatarColor(gov) {
      return govColor(gov);
    }
  }
};
</script>

<style scoped>
.highlighted-keywords {
  font-size: 14px;
  color: black;
}
.highlight {
  background-color: yellow;
  font-style: normal;
}
</style>
